/**
 * FPR Pool Creation Panel
 *
 * Drag-and-drop UI for the pool_creation phase:
 * - Admin clicks "Compute Pools" to auto-assign players
 * - Droppable columns: Leaders, Tank, Healer, DPS, Overflow
 * - Each player is a draggable card with role icon and status
 * - "Confirm Pools" button locks assignments
 * - After confirmation: read-only view with "Begin Draft" inline button
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import { useGameContext } from '../../contexts/GameContext';
import {
  SocketEvent,
  type FprPoolState,
  type FprRegistrationRole,
  type GamePlayerInfo,
  type GameStatus,
} from '@swtorbit/shared-socket-events';
import { getFprRoleIconUrl } from '@swtorbit/shared-assets';

// ============================================================================
// Constants & Helpers
// ============================================================================

type PoolId = 'leaders' | 'tank' | 'healer' | 'dps' | 'overflow';

const POOL_META: Record<PoolId, { label: string; icon: string; color: string }> = {
  leaders: { label: 'Team Leaders', icon: '👑', color: 'var(--color-orange)' },
  tank: { label: 'Tank Pool', icon: '', color: 'var(--color-blue)' },
  healer: { label: 'Healer Pool', icon: '', color: 'var(--color-mint-dark)' },
  dps: { label: 'DPS Pool', icon: '', color: 'var(--color-red)' },
  overflow: { label: 'Overflow', icon: '⚠', color: 'var(--color-slate-dark)' },
};

function RoleIcon({ role, dim }: { role: FprRegistrationRole; dim?: boolean }) {
  return (
    <img
      src={getFprRoleIconUrl(role)}
      alt={role}
      style={{ width: 24, height: 24, objectFit: 'contain', opacity: dim ? 0.25 : 1, transition: 'opacity 0.15s' }}
    />
  );
}

function getPoolForState(poolId: PoolId, state: FprPoolState): string[] {
  switch (poolId) {
    case 'leaders': return state.leaders;
    case 'tank': return state.tankPool;
    case 'healer': return state.healerPool;
    case 'dps': return state.dpsPool;
    case 'overflow': return state.overflow;
  }
}

// ============================================================================
// Player Card Component
// ============================================================================

interface PlayerCardProps {
  playerId: string;
  players: GamePlayerInfo[];
  index: number;
  isDragDisabled: boolean;
  /** The role this player is assigned to in the current pool layout */
  activeRole?: FprRegistrationRole;
}

function PlayerCard({ playerId, players, index, isDragDisabled, activeRole }: PlayerCardProps) {
  const player = players.find(p => p.playerId === playerId);
  const name = player?.characterName || playerId.slice(-8);
  const reg = player?.fprRegistration;
  const isConnected = player?.isConnected ?? false;

  return (
    <Draggable draggableId={playerId} index={index} isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`d-flex align-items-center gap-2xs p-2xs rounded-base ${snapshot.isDragging ? 'border-md border-blue-light' : ''}`}
          style={{
            ...provided.draggableProps.style,
            backgroundColor: snapshot.isDragging ? 'rgba(21, 167, 230, 0.15)' : 'rgba(255,255,255,0.05)',
            marginBottom: '4px',
            cursor: isDragDisabled ? 'default' : 'grab',
          }}
        >
          <span className={`fs-xs ${isConnected ? 'text-green-dark' : 'text-red'}`}>
            {isConnected ? '●' : '○'}
          </span>
          <span className="text-white fs-sm" style={{ flex: 1 }}>{name}</span>
          {reg?.roles.map(r => <RoleIcon key={r} role={r} dim={activeRole !== undefined && r !== activeRole} />)}
          {reg?.knowsMmMechanics && <span className="fs-xs text-amber" title="Knows MM mechanics">MM</span>}
          {reg?.availableAsLeader && <span className="fs-xs" title="Available as leader">👑</span>}
        </div>
      )}
    </Draggable>
  );
}

// ============================================================================
// Pool Column Component
// ============================================================================

interface PoolColumnProps {
  poolId: PoolId;
  playerIds: string[];
  players: GamePlayerInfo[];
  isDragDisabled: boolean;
  /** For the leaders column: maps each leader's playerId to their assigned role */
  leaderRoles?: Record<string, FprRegistrationRole>;
}

function PoolColumn({ poolId, playerIds, players, isDragDisabled, leaderRoles }: PoolColumnProps) {
  const meta = POOL_META[poolId];

  const poolActiveRole: FprRegistrationRole | undefined =
    poolId === 'tank' ? 'tank' :
    poolId === 'healer' ? 'healer' :
    poolId === 'dps' ? 'dps' :
    undefined;

  return (
    <div className="d-flex flex-column" style={{ flex: 1, minWidth: '180px' }}>
      <div className="d-flex align-items-center gap-2xs mb-2xs">
        {poolId === 'tank' && <RoleIcon role="tank" />}
        {poolId === 'healer' && <RoleIcon role="healer" />}
        {poolId === 'dps' && <RoleIcon role="dps" />}
        {meta.icon && <span>{meta.icon}</span>}
        <span className="text-white fs-sm fw-bold">{meta.label}</span>
        <span className="badge badge--sm">{playerIds.length}</span>
      </div>
      <Droppable droppableId={poolId}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="rounded-base p-2xs"
            style={{
              minHeight: '80px',
              backgroundColor: snapshot.isDraggingOver
                ? `${meta.color}22`
                : 'rgba(0,0,0,0.2)',
              border: `1px solid ${snapshot.isDraggingOver ? meta.color : 'color-mix(in srgb, var(--color-white) 10%, transparent)'}`,
              transition: 'background-color 0.2s, border-color 0.2s',
            }}
          >
            {playerIds.map((id, index) => (
              <PlayerCard
                key={id}
                playerId={id}
                players={players}
                index={index}
                isDragDisabled={isDragDisabled}
                activeRole={poolId === 'leaders' ? leaderRoles?.[id] : poolActiveRole}
              />
            ))}
            {provided.placeholder}
            {playerIds.length === 0 && (
              <div className="fs-xs text-slate p-2xs" style={{ textAlign: 'center' }}>
                Drop players here
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

// ============================================================================
// Main Panel Component
// ============================================================================

interface PoolCreationPanelProps {
  players: GamePlayerInfo[];
}

export function PoolCreationPanel({ players }: PoolCreationPanelProps) {
  const { socket } = useSocketContext();
  const { selectedGame } = useGameContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [numTeamsInput, setNumTeamsInput] = useState<number | ''>('');

  const poolState = selectedGame?.fprPoolState ?? null;
  const isConfirmed = poolState?.status === 'confirmed';
  const roomCode = selectedGame?.roomCode;

  const enabledPlayers = useMemo(() => players.filter(p => p.enabled !== false), [players]);

  // Listen for socket responses
  useEffect(() => {
    if (!socket) return;

    const handlePoolsComputed = (data: { success: boolean; poolState?: FprPoolState; error?: string }) => {
      setIsLoading(false);
      if (!data.success) setError(data.error ?? 'Failed to compute pools');
      else setError(null);
    };

    const handlePoolsConfirmed = (data: { success: boolean; error?: string }) => {
      setIsLoading(false);
      if (!data.success) setError(data.error ?? 'Failed to confirm pools');
      else setError(null);
    };

    const handlePoolUpdated = (data: { success: boolean; error?: string }) => {
      if (!data.success) setError(data.error ?? 'Failed to update pool');
    };

    socket.on(SocketEvent.FPR_POOLS_COMPUTED, handlePoolsComputed);
    socket.on(SocketEvent.FPR_POOLS_CONFIRMED, handlePoolsConfirmed);
    socket.on(SocketEvent.FPR_POOL_ASSIGNMENT_UPDATED, handlePoolUpdated);

    return () => {
      socket.off(SocketEvent.FPR_POOLS_COMPUTED, handlePoolsComputed);
      socket.off(SocketEvent.FPR_POOLS_CONFIRMED, handlePoolsConfirmed);
      socket.off(SocketEvent.FPR_POOL_ASSIGNMENT_UPDATED, handlePoolUpdated);
    };
  }, [socket]);

  const handleComputePools = useCallback(() => {
    if (!socket || !roomCode) return;
    setIsLoading(true);
    setError(null);
    socket.emit(SocketEvent.FPR_COMPUTE_POOLS, {
      roomCode,
      numTeams: numTeamsInput || undefined,
    });
  }, [socket, roomCode, numTeamsInput]);

  const handleConfirmPools = useCallback(() => {
    if (!socket || !roomCode) return;
    setIsLoading(true);
    setError(null);
    socket.emit(SocketEvent.FPR_CONFIRM_POOLS, { roomCode });
  }, [socket, roomCode]);

  const handleBeginDraft = useCallback(() => {
    if (!socket || !roomCode) return;
    socket.emit(SocketEvent.ADMIN_UPDATE_GAME_STATUS, {
      roomCode,
      status: 'team_creation' as GameStatus,
    });
  }, [socket, roomCode]);

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination || !socket || !roomCode) return;
    const { draggableId, destination } = result;
    const targetPool = destination.droppableId as PoolId;

    socket.emit(SocketEvent.FPR_UPDATE_POOL_ASSIGNMENT, {
      roomCode,
      playerId: draggableId,
      targetPool,
    });
  }, [socket, roomCode]);

  // ── No pools computed yet ──
  if (!poolState) {
    const suggestedTeams = Math.max(1, Math.floor(enabledPlayers.length / 4));
    return (
      <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm mb-sm">
        <h4 className="mb-xs">Pool Creation</h4>
        <p className="text-white fs-sm mb-sm">
          Compute optimal pool assignments for {enabledPlayers.length} player{enabledPlayers.length !== 1 ? 's' : ''}.
          The system will assign team leaders and sort all players into role pools.
        </p>

        <div className="d-flex align-items-center gap-xs mb-sm flex-wrap">
          <label className="text-white fs-sm">Teams:</label>
          <input
            type="number"
            min={1}
            max={Math.max(1, Math.floor(enabledPlayers.length / 2))}
            value={numTeamsInput}
            onChange={e => setNumTeamsInput(e.target.value ? Number(e.target.value) : '')}
            placeholder={String(suggestedTeams)}
            className="input"
            style={{ width: '70px' }}
          />
          <span className="fs-xs text-slate">(suggested: {suggestedTeams})</span>
        </div>

        {error && <p className="text-red fs-sm mb-xs">⚠ {error}</p>}

        <button
          className="btn"
          onClick={handleComputePools}
          disabled={isLoading || enabledPlayers.length === 0}
        >
          {isLoading ? 'Computing…' : '⚙ Compute Pools'}
        </button>
      </div>
    );
  }

  // ── Pools computed: show DnD interface ──
  const pools: PoolId[] = ['leaders', 'tank', 'healer', 'dps'];
  if (poolState.overflow.length > 0 || !isConfirmed) {
    pools.push('overflow');
  }

  return (
    <div className="d-flex flex-column gap-sm">
      {/* Header */}
      <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-xs">
          <div>
            <h4 style={{ margin: 0 }}>
              Pool Creation {isConfirmed ? '— Confirmed ✅' : '— Review & Adjust'}
            </h4>
            <p className="text-white fs-sm mt-2xs">
              {poolState.numTeams} team{poolState.numTeams !== 1 ? 's' : ''} •{' '}
              {poolState.leaders.length} leader{poolState.leaders.length !== 1 ? 's' : ''} •{' '}
              {poolState.tankPool.length}T / {poolState.healerPool.length}H / {poolState.dpsPool.length}D
              {poolState.overflow.length > 0 && ` • ${poolState.overflow.length} overflow`}
            </p>
          </div>
          <div className="d-flex gap-xs">
            {!isConfirmed && (
              <button
                className="btn btn--outline btn--slate"
                onClick={handleComputePools}
                disabled={isLoading}
              >
                🔄 Recompute
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Warnings */}
      {poolState.warnings.length > 0 && (
        <div className="bg-space bg-o-50 border-md rounded-base p-xs" style={{ borderColor: 'var(--color-bronze)' }}>
          {poolState.warnings.map((w, i) => (
            <p key={i} className="fs-sm" style={{ color: 'var(--color-amber)', margin: i > 0 ? '4px 0 0' : 0 }}>
              ⚠ {w}
            </p>
          ))}
        </div>
      )}

      {error && <p className="text-red fs-sm">⚠ {error}</p>}

      {/* DnD Pool Columns */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="d-flex gap-xs flex-wrap">
          {pools.map(poolId => (
            <PoolColumn
              key={poolId}
              poolId={poolId}
              playerIds={getPoolForState(poolId, poolState)}
              players={enabledPlayers}
              isDragDisabled={isConfirmed}
              leaderRoles={poolId === 'leaders' ? poolState.leaderRoles : undefined}
            />
          ))}
        </div>
      </DragDropContext>

      {/* Action buttons */}
      <div className="d-flex gap-xs align-items-center flex-wrap">
        {!isConfirmed && (
          <button
            className="btn"
            onClick={handleConfirmPools}
            disabled={isLoading}
          >
            {isLoading ? 'Confirming…' : `🔒 Confirm Pools${poolState.warnings.length > 0 ? ` (${poolState.warnings.length} warning${poolState.warnings.length !== 1 ? 's' : ''})` : ''}`}
          </button>
        )}
        {isConfirmed && (
          <button
            className="btn"
            onClick={handleBeginDraft}
          >
            ▶ Begin Draft Phase
          </button>
        )}
      </div>
    </div>
  );
}

export default PoolCreationPanel;
